import React from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import UploadIcon from '@mui/icons-material/Upload';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import LooksOneIcon from '@mui/icons-material/LooksOne';

interface Props {
  file: File | null;
  setFile: (file: File | null) => void;
  isLoading: boolean;
  descriptionText: string;
  infoText: string;
  templateFilename: string;
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
  blockedMessage?: string;
}

const getFileName = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
  return base.length > 30 ? `${base.substring(0, 30)}...${ext}` : name;
};

// Shared single-template form for labour rate dialogs
const BulkUpdateSimpleRatesForm: React.FC<Props> = ({
  file,
  setFile,
  isLoading,
  descriptionText,
  infoText,
  templateFilename,
  onDownloadTemplate,
  isDownloadingTemplate,
  blockedMessage,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const tilesDisabled = isLoading || isDownloadingTemplate;

  return (
    <DialogContent>
      <Typography variant="body2" sx={{ marginBottom: '1rem' }}>
        {descriptionText}
      </Typography>

      <Alert severity="info" sx={{ marginBottom: '1rem', textAlign: 'justify' }}>
        {infoText}
      </Alert>

      {blockedMessage ? (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          {blockedMessage}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, borderRadius: '8px' }}>
          <ButtonBase sx={{ flex: 1 }} disabled={tilesDisabled} onClick={onDownloadTemplate}>
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksOneIcon
                sx={{ position: 'absolute', top: 10, left: 10, color: '#737F9C' }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                {isDownloadingTemplate ? (
                  <CircularProgress size={32} sx={{ color: '#737F9C' }} />
                ) : (
                  <DownloadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                )}
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Download template
                </Typography>
                <Chip
                  label={templateFilename}
                  sx={{ marginTop: 1, bgcolor: '#F4F5F9', color: '#737F9C' }}
                  icon={<DownloadIcon />}
                />
              </Stack>
            </Box>
          </ButtonBase>

          <ButtonBase component="label" disabled={tilesDisabled} sx={{ flex: 1, borderRadius: '8px' }}>
            <input
              type="file"
              accept=".xlsx"
              disabled={tilesDisabled}
              hidden
              onChange={(e) => {
                const selected = e.target.files?.[0];
                e.target.value = '';
                if (!selected) return;
                if (!selected.name.toLowerCase().endsWith('.xlsx')) {
                  enqueueSnackbar('Please select an .xlsx file', { variant: 'error' });
                  return;
                }
                setFile(selected);
              }}
            />
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksTwoIcon
                sx={{ position: 'absolute', top: 10, left: 10, color: '#737F9C' }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                <CloudUploadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Upload .xlsx
                </Typography>
                <Typography variant="caption">Uploaded file must follow the template.</Typography>
                {file && (
                  <Chip
                    label={getFileName(file.name)}
                    sx={{ marginTop: 1, bgcolor: '#F4F5F9', color: '#737F9C' }}
                    icon={<UploadIcon />}
                  />
                )}
              </Stack>
            </Box>
          </ButtonBase>
        </Box>
      )}
      {isLoading && (
        <Box mt={2} display="flex" justifyContent="center" flexDirection="column">
          <Typography variant="caption" color="textSecondary">
            This process may take a while depending on the size of the file.
          </Typography>
          <LinearProgress sx={{ width: '100%', marginTop: 5 }} />
        </Box>
      )}
    </DialogContent>
  );
};

export default BulkUpdateSimpleRatesForm;
